import { useForm, Controller } from 'react-hook-form';
import Dropdown from '@cp/components/Dropdown';
import BorderedInput from '@primitives/BorderedInput';
import { Button, TextArea, Typography } from '@primitives';
import UploadComponent from '@cp/components/UploadComponent/UploadComponent';
import useUpload from '@cp/components/UploadComponent/useUpload';
import RadioGroup from '@components/RadioGroup';
import axiosInstance, { ApiKeys, getApiUrl } from '@cp/utils/ApiHelper';
import UploadSuccessComponent from '@cp/components/UploadComponent/UploadSuccessComponent';
import { getFileDownload } from '@cp/utils/commonUtils';
import React, { useState } from 'react';
import { toast } from '@primitives/Toast';
import LoaderIcon from '@cp/assets/icons/LoaderIcon';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { AGENT_AUTO_LOGOUT_CLICKSTREAM } from '@cp/pages/SelfServeStateResetter/constants';
import {
  dropDownOptions,
  Inputs,
  Labels,
  PORTAL,
  TYPE_OF_UPLOAD
} from '@cp/pages/SelfServeStateResetter/types';
import { onFormSubmit, onSampleFileDownload } from '@cp/pages/SelfServeStateResetter/actions';

const Form = () => {
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isValid }
  } = useForm<Inputs>({
    defaultValues: {
      typeOfUpload: 'manual',
      platform: {
        value: Labels.ALL,
        label: Labels.ALL
      }
    },
    mode: 'onChange'
  });
  const { file, setFile, setError } = useUpload({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const onSubmit = (data: Inputs) => {
    setIsSubmitting(true);
    const { agentList, justification, platform, typeOfUpload } = data;
    const form = new FormData();
    form.append('portal', PORTAL);
    form.append('platform', platform?.value?.toUpperCase());
    if (typeOfUpload === TYPE_OF_UPLOAD.MANUAL) {
      form.append('agentReferenceIds', agentList);
      form.append('bulkReset', 'false');
    } else {
      form.append('bulkReset', 'true');
    }

    if (typeOfUpload !== TYPE_OF_UPLOAD.MANUAL && file) {
      form.append('file', file);
    }
    onFormSubmit({
      form: form,
      onSuccessCb: () => {
        reset();
        setFile(null);
      },
      onFailureCb: () => {
        toast.error('Error submitting the form. Please try again.', err?.message);
      },
      onFinalCb: () => {
        setIsSubmitting(false);
        addClickstreamEvent(AGENT_AUTO_LOGOUT_CLICKSTREAM.LH_AUTO_LOGOUT_TRIGGERED, {
          platform: platform?.value,
          agentList: file?.name || agentList,
          justification
        });
      }
    });
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-2 mt-4 max-h-[500px] transition-all">
        <Typography variant={'p4'}>Select Agents </Typography>
        <Controller
          control={control}
          render={({ field }) => (
            <RadioGroup
              defaultValue={field.value}
              name="typeOfUpload"
              onChange={(e: React.ChangeEvent<any>) => {
                field.onChange(e.target.value);
              }}
              value={field.value}
              options={[
                {
                  id: 'manual',
                  label: 'Manual',
                  value: 'manual'
                },
                {
                  id: 'bulk',
                  label: 'Bulk',
                  value: 'bulk'
                }
              ]}
            />
          )}
          name="typeOfUpload"
        />

        {watch('typeOfUpload') === 'manual' ? (
          <Controller
            render={({ field, fieldState, formState }) => (
              <TextArea
                placeholder="Comma separated agent ids"
                containerClassName="w-full"
                value={field.value}
                onChange={field.onChange}
                error={errors.agentList?.message}
              />
            )}
            rules={{ required: 'agent list  is required' }}
            name="agentList"
            control={control}
          />
        ) : (
          <>
            {file ? (
              <UploadSuccessComponent
                name={file?.name}
                onCancel={() => setFile(null)}
                containerClassName={'!m-0 !mt-2 !px-2 '}
              />
            ) : (
              <UploadComponent
                setFile={setFile}
                file={file}
                setHasError={setError}
                supportedFile={'.csv'}
                containerClassName="!m-0 !mt-4"
                isSampleFileAvailable={true}
                onSampleFileDownload={onSampleFileDownload}
              />
            )}
          </>
        )}
        <Typography variant={'p4'} className="mt-4">
          Choose platform{' '}
        </Typography>
        <Controller
          name="platform"
          control={control}
          render={({ field, fieldState, formState }) => {
            return (
              <Dropdown
                options={dropDownOptions}
                defaultSelected={dropDownOptions[0]}
                value={field.value}
                onSelectionChange={field.onChange}
                placeholder={'Select platform'}
              />
            );
          }}
        />

        <Typography variant={'p4'} className="mt-4">
          Justification{' '}
        </Typography>
        <Controller
          rules={{ required: 'Justification is required' }}
          control={control}
          render={({ field }) => (
            <BorderedInput
              placeholder={'slash issue which requires logout'}
              containerClassName="w-full mt-0"
              onChange={event => field.onChange(event?.target.value)}
              value={field.value}
              error={errors.justification?.message}
            />
          )}
          name="justification"
        />

        <div className="self-end mt-4">
          <Button disabled={!isValid || isSubmitting}>
            {isSubmitting ? (
              <div className="w-[45px]">
                <LoaderIcon
                  size={20}
                  borderWidth={2}
                  borderColor="var(--text-primary)"
                  fillColor="var(--blue-base)"
                />
              </div>
            ) : (
              <div>Submit </div>
            )}{' '}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default Form;
